import { EventAggregator } from 'aurelia-event-aggregator';
import { bindable, computedFrom } from 'aurelia-framework';
import {
	autoinject,
	BindingEngine,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

import { Field } from '~/components/field/common/field';
import { Form } from '~/components/form/form';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel } from '~/models/form-model';

import Inputmask from 'inputmask';
import { convertInputMask } from '~/inputmasks/input-mask-converter';

const componentTagName = 'a-tfn';

registerComponent(componentTagName, {
	name: 'Tax File Number',
	requirePath: PLATFORM.moduleName('components/field/custom/tfn'),
	props: [
		...commonFieldProperties,
		{
			label: 'Supplied Field Model',
			name: 'tfnSuppliedFieldModel',
			type: 'FieldModel',
		},
		{
			label: 'Status Field Model',
			name: 'tfnStatusFieldModel',
			type: 'FieldModel',
		},
	],
	isSelectable: true,
});

@autoinject
@customElement(componentTagName)
@useView(PLATFORM.moduleName('./tfn.html'))
export class TFNField extends Field {
	/**
	 * `ZZz_Tfn_Supplied` FieldModel
	 */
	@bindable protected tfnSuppliedFieldModel: FieldModel;
	/**
	 * `ZZz_Tfn_Valid_Status` FieldModel
	 */
	@bindable protected tfnStatusFieldModel: FieldModel;

	constructor(
		public element: Element,
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
		protected bindingEngine: BindingEngine,
		protected i18n: I18N,
	) {
		super(Field.TYPE.TEXT, element, parentForm, eventAggregator);
	}

	public attached() {
		super.attached();

		this.eventSubscriptions.push(
			this.bindingEngine
				.propertyObserver(this, 'displayValue')
				.subscribe(() => (this.placeholder = this.displayValue)),
		);
	}

	/**
	 * Override
	 */
	@computedFrom('fieldModel', 'fieldModel.inputMask')
	get inputmaskOptions(): inputmask.InputmaskOptions {
		// York: if we get blank inputmask from server, we have a default one.
		let mask = '999-999-99[9]';
		if (this.fieldModel && this.fieldModel.inputMask) {
			mask = convertInputMask(this.fieldModel.inputMask);
		}
		return {
			autoUnmask: false,
			clearMaskOnLostFocus: true,
			placeholder: '0',
			clearIncomplete: false,
			mask,
		};
	}

	/**
	 * Checks if the TFN format is valid
	 */
	private isFormatValid = (tfn: string) =>
		/^\d{3}-?\d{3}-?\d{2,3}$/g.test(tfn);

	@computedFrom(
		'tfnSuppliedFieldModel',
		'tfnSuppliedFieldModel.value',
		'tfnStatusFieldModel.value',
	)
	get displayValue() {
		if (this.tfnSuppliedFieldModel) {
			if (
				this.tfnSuppliedFieldModel.value === 'tfn.supplied' &&
				this.tfnStatusFieldModel &&
				this.tfnStatusFieldModel.value
			) {
				// TFN is SUPPLIED and there is a STATUS
				return this.i18n.tr(this.tfnStatusFieldModel.value);
			}
			// TFN is NOT SUPPLIED (or SUPPLIED but without a status?)
			return this.i18n.tr(this.tfnSuppliedFieldModel.value);
		}
		return '';
	}
}
